/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Toast } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { accessAPI, Titulo, Boton, Sidebar } from "../../../../shared";
import GestionAccesos from "../../../../elementos/GestionAccesos";

/**
 * Botón de usuario del colaborador, para el título de su ficha.
 *
 * Un colaborador puede existir sin usuario —se da de alta para asignarle
 * actividades— y recién después necesitar acceso. El usuario se genera con el
 * email que ya tiene cargado el colaborador; no se pide ninguno aparte para que
 * no queden dos mails distintos para la misma persona.
 *
 * Vive dentro de `botones` del Titulo, así que todo lo que muestra —sidebar y
 * diálogo— se portalea fuera del árbol y no rompe el layout del encabezado.
 */
export default function BotonUsuario({ auditor }) {
  const auditorId = auditor?.id;

  const [usuario, setUsuario] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [generando, setGenerando] = useState(false);
  const [sideBarAccesos, setSideBarAccesos] = useState(false);
  const [linkAlta, setLinkAlta] = useState(null);

  const toast = useRef(null);

  useEffect(() => {
    if (!auditorId) return;
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditorId]);

  function cargar() {
    setCargando(true);
    accessAPI(
      "GET",
      `admin/auditor/${auditorId}/usuario`,
      null,
      (respuesta) => {
        setUsuario(respuesta.usuario);
        setCargando(false);
      },
      () => setCargando(false)
    );
  }

  function generarUsuario() {
    if (!auditor?.email) {
      toast.current?.show({
        severity: "warn",
        summary: "Falta el email",
        detail:
          "El colaborador no tiene email cargado. Agregalo en Detalles y volvé a intentar.",
        life: 6000
      });
      return;
    }

    setGenerando(true);
    accessAPI(
      "POST",
      `admin/auditor/${auditorId}/usuario`,
      { email: auditor.email },
      (respuesta) => {
        setGenerando(false);
        setUsuario(respuesta.usuario);
        if (respuesta.usuarioExistente) {
          toast.current?.show({
            severity: "info",
            summary: "Usuario existente",
            detail:
              "Ya había un usuario con ese email en el organismo. Se le agregó el acceso de colaborador y entra con la contraseña que ya usaba.",
            life: 8000
          });
        } else {
          setLinkAlta(respuesta.linkAltaPassword);
        }
      },
      (respuesta) => {
        setGenerando(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta?.[0]?.msg || "No se pudo generar el usuario",
          sticky: true
        });
      }
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />

      <Sidebar
        visible={sideBarAccesos}
        position="right"
        onHide={() => setSideBarAccesos(false)}
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Gestionar accesos</Titulo>
        {sideBarAccesos && usuario && (
          <GestionAccesos
            usuarioId={usuario.id}
            toast={toast}
            onAccesoEliminado={() => cargar()}
            onUsuarioEliminado={() => {
              setSideBarAccesos(false);
              setUsuario(null);
            }}
          />
        )}
      </Sidebar>

      <Dialog
        header="Usuario generado"
        visible={!!linkAlta}
        onHide={() => setLinkAlta(null)}
        className="w-full md:w-30rem"
      >
        <p css={{ marginTop: 0 }}>
          Pasale este link al colaborador para que defina su contraseña — no se
          vuelve a mostrar.
        </p>
        <div css={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <code
            css={{
              flex: 1,
              minWidth: 0,
              wordBreak: "break-all",
              background: "rgba(0,0,0,0.05)",
              padding: "0.5rem",
              borderRadius: "4px"
            }}
          >
            {linkAlta}
          </code>
          <div css={{ flexShrink: 0 }}>
            <Boton
              verde
              transparente
              callback={() => {
                navigator.clipboard?.writeText(linkAlta);
                toast.current?.show({
                  severity: "info",
                  summary: "Copiado",
                  life: 2000
                });
              }}
            >
              Copiar
            </Boton>
          </div>
        </div>
      </Dialog>

      {/* Mientras no se sepa si tiene usuario no se muestra nada: aparecer
          como "Generar usuario" y cambiar a "Gestionar accesos" al terminar de
          cargar se vería como un parpadeo. */}
      {!cargando && !usuario && (
        <Boton
          transparente
          loader={generando}
          confirmacion={
            auditor?.email
              ? `¿Generar el usuario del colaborador con el email ${auditor.email}? Va a recibir un link para definir su contraseña.`
              : undefined
          }
          callback={generarUsuario}
        >
          Generar usuario
        </Boton>
      )}

      {!cargando && usuario && (
        <Boton transparente callback={() => setSideBarAccesos(true)}>
          Gestionar accesos
        </Boton>
      )}
    </>
  );
}
